import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import AlbumRoundedIcon from "@mui/icons-material/AlbumRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function MasteringBasicsLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Mastering Basics in Music Production: What It Is and How It Works",
      description:
        "Learn what mastering is, what happens in a mastering chain, how mastering differs from mixing, and the basics of EQ, compression, limiting, loudness, stereo imaging, and final delivery.",
      author: {
        "@type": "Organization",
        name: "Parish Audio",
      },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage: "https://parishaudio.com/learning/mastering-basics",
      articleSection: "Audio Education",
      keywords: [
        "mastering basics",
        "what is mastering",
        "music mastering",
        "mastering vs mixing",
        "mastering chain",
        "audio mastering basics",
        "how to master music",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Mastering is the final stage of audio production, where a finished mix is refined for tone, loudness, consistency, translation, and final release.",
      icon: <AlbumRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "Good mastering helps a song sound polished, balanced, and reliable across headphones, speakers, cars, phones, and streaming platforms.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Singles, albums, EPs, streaming releases, physical media, film music, podcasts, and any final audio that needs dependable playback.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const masteringStages = [
    {
      name: "Critical Listening",
      description:
        "Mastering begins by listening carefully to the mix as a whole and deciding what it truly needs rather than processing it out of habit.",
    },
    {
      name: "Tone Shaping",
      description:
        "Subtle EQ may be used to improve balance, remove buildup, or add a small amount of clarity, weight, or openness.",
    },
    {
      name: "Dynamic Control",
      description:
        "Compression, limiting, or other dynamics tools may be used to control peaks, shape glue, and manage loudness.",
    },
    {
      name: "Stereo Refinement",
      description:
        "Stereo width, mid/side balance, and spatial presentation may be adjusted carefully to improve focus or openness.",
    },
    {
      name: "Level and Loudness",
      description:
        "The master is brought to an appropriate final level while avoiding unnecessary damage to punch, clarity, and musicality.",
    },
    {
      name: "Final Delivery",
      description:
        "The finished file is exported in the required format, checked for technical issues, and prepared for streaming, download, or physical release.",
    },
  ];

  const masteringConcepts = [
    {
      name: "Mastering Is for the Full Mix",
      description:
        "Mastering works on the finished stereo mix or final mix bus, not on isolated tracks the way mixing does.",
    },
    {
      name: "Small Moves Matter",
      description:
        "Mastering decisions are often subtle. Tiny adjustments can make a noticeable difference because they affect the whole song at once.",
    },
    {
      name: "Translation Matters",
      description:
        "A strong master should hold up across many playback systems, not just the studio monitors it was created on.",
    },
    {
      name: "Musicality Comes First",
      description:
        "The goal is not to make a track impress with meters alone. The goal is to preserve or improve the emotional impact of the music.",
    },
  ];

  const controls = [
    {
      name: "EQ",
      description:
        "Used for broad tonal adjustments, such as cleaning up muddiness, smoothing harshness, or adding a little air or weight.",
    },
    {
      name: "Compression",
      description:
        "Can add glue, consistency, or movement when used subtly across the full mix.",
    },
    {
      name: "Limiter",
      description:
        "Controls peaks and helps set final loudness, often at the end of the mastering chain.",
    },
    {
      name: "Stereo Imaging",
      description:
        "Used carefully to shape width and focus, often with attention to mono compatibility and low-end stability.",
    },
    {
      name: "Saturation / Color",
      description:
        "Sometimes used in very small amounts to add density, smoothness, or analog-style character.",
    },
    {
      name: "Metering",
      description:
        "Helps track loudness, peak level, true peak behavior, stereo balance, and overall technical health of the master.",
    },
  ];

  const commonUses = [
    "Making a finished mix sound more polished and release-ready",
    "Improving balance so the song translates better on different playback systems",
    "Matching songs more consistently across an EP or album",
    "Setting a final loudness target without destroying the mix",
    "Catching technical issues before release",
    "Preparing final files for streaming, download, or physical formats",
  ];

  const bestPractices = [
    "Make sure the mix is truly finished before mastering. Mastering cannot replace a strong mix.",
    "Use subtle moves first. If the master needs extreme fixing, the real issue may be back in the mix.",
    "Level-match when comparing processed and unprocessed versions so louder does not fool you.",
    "Reference other well-mastered tracks in a similar style, but do not chase numbers at the expense of musicality.",
    "Check the master on more than one playback system before release.",
  ];

  const relatedLinks = [
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "Learn how EQ shapes tonal balance, which is one of the most common parts of mastering.",
    },
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "Explore how compression can add glue and control when used carefully on a full mix.",
    },
    {
      title: "Limiting",
      to: "/learning/limiting",
      description:
        "See how limiting controls peaks and helps set final loudness in a mastering chain.",
    },
    {
      title: "Stereo Imaging",
      to: "/learning/stereo-imaging",
      description:
        "Understand how stereo width and focus affect how open and stable a master feels.",
    },
  ];

  const sectionCardSx = {
    p: 2,
    borderRadius: 3,
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  return (
    <>
      <Seo
        title="Mastering Basics | Parish Audio Learning"
        description="Learn what mastering is, what happens in a mastering chain, how mastering differs from mixing, and the basics of EQ, compression, limiting, loudness, stereo imaging, and final delivery."
        canonical="https://parishaudio.com/learning/mastering-basics"
        schema={articleSchema}
      />

      <Box ref={topContainer} sx={{ width: "95%", maxWidth: 1100, mx: "auto", minHeight: height - 140 }}>
        <Paper
          sx={{
            mt: 3,
            mb: 4,
            p: { xs: 2, md: 3 },
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
            borderRadius: 3,
            color: style.color4,
            boxShadow: 12,
            overflow: "hidden",
          }}
        >
          <Stack spacing={3.25}>
            <Box>
              <Chip
                label="Learning Center"
                sx={{
                  mb: 1.5,
                  color: style.color4,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  mb: 1.25,
                  color: style.color4,
                  maxWidth: 900,
                }}
              >
                Mastering Basics in Music Production: What It Is and How It Works
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  maxWidth: 950,
                }}
              >
                Mastering is the final stage of music production. After the song has been
                recorded, produced, and mixed, mastering is the process of refining that
                finished mix so it is balanced, polished, and ready for release. It is
                where the entire song is treated as one complete piece rather than a group
                of separate tracks.
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  mt: 2,
                  maxWidth: 950,
                }}
              >
                Good mastering can improve clarity, consistency, punch, loudness, and
                translation across different playback systems. It can also help songs sit
                together more cohesively on an album or EP. Just as important, mastering is
                often the last opportunity to catch technical problems before a track is
                released to the world.
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="stretch">
              {quickFacts.map((item) => (
                <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                  <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 6 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box sx={{ flexShrink: 0 }}>{item.icon}</Box>
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: style.color3,
                            lineHeight: 1.7,
                            fontSize: "0.97rem",
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                What is mastering?
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                Mastering is the process of optimizing the final mix for playback, release,
                and consistency. Instead of adjusting individual instruments, the mastering
                engineer works with the complete mix and makes subtle decisions that affect
                the whole song at once.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                These decisions may include tonal shaping, dynamic control, loudness
                management, stereo refinement, sequencing, spacing between songs, and
                preparing final export formats. In many modern workflows, mastering also
                includes checking how the song will behave on streaming platforms and across
                real-world listening environments.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                Mastering is often described as the final quality-control and presentation
                stage. It does not usually reinvent the mix. Instead, it helps the mix reach
                its best final form.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                How mastering works
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {masteringConcepts.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Typography sx={{ color: accent, fontWeight: 700, mb: 0.75 }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                Because mastering affects the full song, even small adjustments can be
                powerful. A gentle EQ change may clarify the whole record. A little
                compression may improve cohesiveness. A limiter may raise the final level.
                The challenge is knowing how much is enough without damaging the mix.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Common stages in a mastering process
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {masteringStages.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 4 }}>
                      <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Mastering vs mixing
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                      Same overall production path, different job
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Mixing is about balancing and shaping the individual elements inside a
                    song. That includes levels, panning, EQ, compression, reverb, delay,
                    automation, and creative decisions between tracks.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Mastering happens after the mix is finished. It focuses on the final
                    stereo mix as a complete whole. If a vocal is too loud compared to the
                    snare, that is mainly a mixing issue. If the full song needs a touch
                    more openness, consistency, or final level control, that is where
                    mastering comes in.
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Common mastering tools
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {controls.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                            {item.name}
                          </Typography>
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                What mastering is commonly used for
              </Typography>

              <Paper sx={{ p: 2.25, borderRadius: 3, backgroundColor: panelBg }}>
                <Stack spacing={1.35}>
                  {commonUses.map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <LibraryMusicRoundedIcon
                        sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                      />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                One important part of mastering is translation. A mix that sounds good in
                one room can behave very differently in a car, on earbuds, or on a small
                Bluetooth speaker. Mastering aims to make the song feel dependable across
                those environments, not just impressive in one ideal setup.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Common mastering mistakes
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.35}>
                  {[
                    "Trying to fix major mix problems in mastering instead of going back to the mix.",
                    "Pushing loudness so hard that the track loses punch, depth, and emotional impact.",
                    "Using too much stereo widening and creating phase or mono compatibility issues.",
                    "Making broad EQ moves without confirming that the change truly improves the full song.",
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <WarningAmberRoundedIcon
                        sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                      />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Best practices for mastering
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {bestPractices.map((item, index) => (
                  <Grid item xs={12} md={6} key={index} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TipsAndUpdatesRoundedIcon
                          sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                        />
                        <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                          {item}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Related learning topics
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {relatedLinks.map((item) => (
                  <Grid item xs={12} md={6} key={item.title} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: panelBg,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                          {item.title}
                        </Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}>
                          {item.description}
                        </Typography>
                      </Box>

                      <Box>
                        <Button
                          component={RouterLink}
                          to={item.to}
                          variant="outlined"
                          sx={{
                            color: style.color4,
                            borderColor: "rgba(255,255,255,0.18)",
                            "&:hover": {
                              borderColor: accent,
                              backgroundColor: "rgba(255,255,255,0.04)",
                            },
                          }}
                        >
                          Read More
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Final thoughts
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.9, mb: 2 }}>
                Mastering is where a finished mix becomes a finished release. It is about
                polish, consistency, technical readiness, and making sure the music holds up
                in the real world.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                The best mastering usually does not feel heavy-handed. It supports the song,
                preserves its strengths, and helps it translate with confidence. When done
                well, mastering can make a track feel complete.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}